'use client';

/**
 * useExchangeRate Hook
 *
 * React hook for real-time exchange rate data from Firestore.
 * Provides CLP to USD conversion functions for displaying dual prices.
 *
 * Features:
 * - Real-time updates via Firestore onSnapshot
 * - Fallback to hardcoded rate (900) if no data
 * - Stale rate detection
 * - Utility functions for conversion
 *
 * @example
 * ```tsx
 * const { rate, loading, convertCLPtoUSD } = useExchangeRate();
 *
 * // Display dual price
 * const clpPrice = 334900;
 * const usdPrice = convertCLPtoUSD(clpPrice);
 * // -> "CLP $334,900 ≈ USD $372.11"
 * ```
 *
 * @module useExchangeRate
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, onSnapshot, Firestore } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ExchangeRateData } from '@/types/booking';

// ============================================================================
// Constants
// ============================================================================

/** Fallback rate if Firestore data unavailable */
const FALLBACK_RATE = 900;
/** Fallback BRL rate */
const FALLBACK_BRL_RATE = 5.70;

/** Firestore document path */
const EXCHANGE_RATE_DOC = 'settings/exchangeRates';

// ============================================================================
// Types
// ============================================================================

export interface UseExchangeRateResult {
  /** USD to CLP exchange rate (e.g., 900.12) */
  rate: number;
  /** CLP to USD exchange rate (e.g., 0.001111) */
  inverseRate: number;
  /** USD to BRL exchange rate (e.g., 5.70) */
  brlRate: number;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether rate is stale/expired */
  isStale: boolean;
  /** Whether using fallback rate */
  isFallback: boolean;
  /** Source of the rate */
  source: string;
  /** When rate was last fetched */
  fetchedAt: Date | null;
  /** Convert CLP amount to USD */
  convertCLPtoUSD: (clpAmount: number) => number;
  /** Convert USD amount to CLP */
  convertUSDtoCLP: (usdAmount: number) => number;
  /** Convert USD amount to BRL */
  convertUSDtoBRL: (usdAmount: number) => number;
  /** Full rate data from Firestore */
  rateData: ExchangeRateData | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert CLP to USD
 */
function clpToUsd(clpAmount: number, usdToClpRate: number): number {
  if (usdToClpRate <= 0) return 0;
  // Round to 2 decimal places
  return Math.round((clpAmount / usdToClpRate) * 100) / 100;
}

/**
 * Convert USD to CLP
 */
function usdToClp(usdAmount: number, usdToClpRate: number): number {
  if (usdToClpRate <= 0) return 0;
  // Round to whole number (CLP doesn't have cents)
  return Math.round(usdAmount * usdToClpRate);
}

/**
 * Convert USD to BRL
 */
function usdToBrl(usdAmount: number, usdToBrlRate: number): number {
  if (usdToBrlRate <= 0) return 0;
  // Round to 2 decimal places
  return Math.round(usdAmount * usdToBrlRate * 100) / 100;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useExchangeRate - Real-time exchange rate hook
 *
 * Subscribes to Firestore document at settings/exchangeRates and provides
 * conversion utilities for displaying dual prices (CLP + USD).
 */
export function useExchangeRate(): UseExchangeRateResult {
  // State
  const [rateData, setRateData] = useState<ExchangeRateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to Firestore document
  useEffect(() => {
    // Check if db is available (client-side only)
    if (!db) {
      setLoading(false);
      setError('Firebase not initialized');
      return;
    }

    const docRef = doc(db as Firestore, EXCHANGE_RATE_DOC);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        setLoading(false);

        if (!snapshot.exists()) {
          setError('Exchange rate not found');
          setRateData(null);
          return;
        }

        const data = snapshot.data() as ExchangeRateData;
        setRateData(data);
        setError(null);
      },
      (err) => {
        console.error('[useExchangeRate] Firestore error:', err);
        setLoading(false);
        setError(err.message);
        setRateData(null);
      }
    );

    return () => unsubscribe();
  }, []);

  // Computed values. Reject non-positive stored rates (a corrupt usdToClp: 0
  // would make every USD-derived total collapse to 0 and, since the pay-link
  // modal enforces a US$0.50 floor client-side, block valid lodge-only links)
  // — mirror the backend's getExchangeRateOrDefault, which only trusts > 0.
  const rate = useMemo(() => {
    const r = rateData?.usdToClp;
    return typeof r === 'number' && r > 0 ? r : FALLBACK_RATE;
  }, [rateData?.usdToClp]);

  const inverseRate = useMemo(() => {
    return 1 / rate;
  }, [rate]);

  const brlRate = useMemo(() => {
    const r = rateData?.usdToBrl;
    return typeof r === 'number' && r > 0 ? r : FALLBACK_BRL_RATE;
  }, [rateData?.usdToBrl]);

  const isStale = useMemo(() => {
    if (!rateData?.expiresAt) return true;

    const expiresAt = typeof rateData.expiresAt === 'string'
      ? new Date(rateData.expiresAt)
      : rateData.expiresAt instanceof Date
        ? rateData.expiresAt
        : rateData.expiresAt.toDate();

    return new Date() > expiresAt;
  }, [rateData?.expiresAt]);

  const isFallback = useMemo(() => {
    return !rateData || rateData.fallbackUsed || rate === FALLBACK_RATE;
  }, [rateData, rate]);

  const source = useMemo(() => {
    if (!rateData) return 'fallback';
    return rateData.source;
  }, [rateData]);

  const fetchedAt = useMemo(() => {
    if (!rateData?.fetchedAt) return null;

    if (typeof rateData.fetchedAt === 'string') {
      return new Date(rateData.fetchedAt);
    }
    if (rateData.fetchedAt instanceof Date) {
      return rateData.fetchedAt;
    }
    // Firestore Timestamp
    return rateData.fetchedAt.toDate();
  }, [rateData?.fetchedAt]);

  // Conversion functions (memoized)
  const convertCLPtoUSD = useCallback(
    (clpAmount: number): number => {
      return clpToUsd(clpAmount, rate);
    },
    [rate]
  );

  const convertUSDtoCLP = useCallback(
    (usdAmount: number): number => {
      return usdToClp(usdAmount, rate);
    },
    [rate]
  );

  const convertUSDtoBRL = useCallback(
    (usdAmount: number): number => {
      return usdToBrl(usdAmount, brlRate);
    },
    [brlRate]
  );

  // Return memoized result
  return useMemo(
    () => ({
      rate,
      inverseRate,
      brlRate,
      loading,
      error,
      isStale,
      isFallback,
      source,
      fetchedAt,
      convertCLPtoUSD,
      convertUSDtoCLP,
      convertUSDtoBRL,
      rateData,
    }),
    [
      rate,
      inverseRate,
      brlRate,
      loading,
      error,
      isStale,
      isFallback,
      source,
      fetchedAt,
      convertCLPtoUSD,
      convertUSDtoCLP,
      convertUSDtoBRL,
      rateData,
    ]
  );
}

/**
 * Utility hook for simple exchange rate access (no real-time updates)
 * Fetches rate once via API endpoint
 */
export function useExchangeRateOnce(): {
  rate: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [rate, setRate] = useState(FALLBACK_RATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiBase = process.env.NEXT_PUBLIC_API_URL || '';
      const response = await fetch(`${apiBase}/api/exchange-rates`);

      if (!response.ok) {
        throw new Error('Failed to fetch exchange rate');
      }

      const data = await response.json();

      if (data.success && data.data?.usdToClp) {
        setRate(data.data.usdToClp);
      }
    } catch (err) {
      console.error('[useExchangeRateOnce] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch rate');
      // Keep using fallback rate
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  return {
    rate,
    loading,
    error,
    refresh: fetchRate,
  };
}

export default useExchangeRate;
